import React, { useState, useEffect } from 'react'
import { simulacionesGpsService } from '../services/simulacionesGpsService'
import { toast } from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { formatearFechaParaMostrar } from '../utils/dateUtils'
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Copy,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  RotateCcw,
  Calendar
} from 'lucide-react'

const PAGE_SIZE = 10
const columnasOrdenables = [
  'id', 'fechaLanzamiento', 'imei', 'imei_real', 'placa', 'estado_replica', 'fechaHoraFinal'
]

const GestionReplicasGPS = () => {
  const { hasPermission } = useAuth()
  const [replicas, setReplicas] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedReplica, setSelectedReplica] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [nuevaFechaHoraFinal, setNuevaFechaHoraFinal] = useState('')
  const [filtros, setFiltros] = useState(() => {
    const saved = localStorage.getItem('gps_replicas_filtros')
    return saved ? JSON.parse(saved) : {
      estado_replica: '',
      fecha_inicio: '',
      fecha_fin: '',
      busqueda: ''
    }
  })
  // Paginación y ordenamiento
  const [pagina, setPagina] = useState(1)
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'id', direccion: 'desc' })

  // Cargar réplicas
  const cargarReplicas = async () => {
    try {
      setLoading(true)
      const response = await simulacionesGpsService.getReplicas(filtros)
      setReplicas(response.data)
    } catch (error) {
      console.error('Error cargando réplicas:', error)
      toast.error('Error al cargar las réplicas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    cargarReplicas()
    localStorage.setItem('gps_replicas_filtros', JSON.stringify(filtros))
    setPagina(1) // Resetear página al cambiar filtros
  }, [filtros])

  // Ordenamiento frontend
  const ordenarReplicas = (arr) => {
    const { campo, direccion } = ordenamiento
    return [...arr].sort((a, b) => {
      let valA = a[campo]
      let valB = b[campo]
      // Ordenar id como número
      if (campo === 'id') {
        valA = parseInt(valA)
        valB = parseInt(valB)
        if (isNaN(valA)) valA = 0
        if (isNaN(valB)) valB = 0
      } else {
        if (valA === null || valA === undefined) valA = ''
        if (valB === null || valB === undefined) valB = ''
        valA = String(valA)
        valB = String(valB)
      }
      if (valA < valB) return direccion === 'asc' ? -1 : 1
      if (valA > valB) return direccion === 'asc' ? 1 : -1
      return 0
    })
  }

  // Paginación frontend
  const replicasOrdenadas = ordenarReplicas(replicas)
  const totalPaginas = Math.ceil(replicasOrdenadas.length / PAGE_SIZE)
  const replicasPagina = replicasOrdenadas.slice((pagina - 1) * PAGE_SIZE, pagina * PAGE_SIZE)

  // Cargar réplica por ID
  const cargarReplica = async (id) => {
    try {
      const response = await simulacionesGpsService.getSimulacion(id)
      setSelectedReplica(response.data)
      setShowModal(true)
    } catch (error) {
      console.error('Error cargando réplica:', error)
      toast.error('Error al cargar la réplica')
    }
  }

  // Eliminar réplica
  const eliminarReplica = async () => {
    try {
      await simulacionesGpsService.deleteSimulacion(deleteId)
      toast.success('Réplica eliminada exitosamente')
      setShowDeleteModal(false)
      setDeleteId(null)
      cargarReplicas()
    } catch (error) {
      console.error('Error eliminando réplica:', error)
      toast.error('Error al eliminar la réplica')
    }
  }

  // Duplicar réplica
  const duplicarReplica = async (id) => {
    try {
      // Obtener los datos de la réplica seleccionada
      const replica = replicasPagina.find(r => r.id === id)
      if (!replica) {
        toast.error('No se encontró la réplica')
        return
      }

      // Crear un objeto con los datos de la réplica para el formulario
      const datosReplica = {
        fechaLanzamiento: replica.fechaLanzamiento,
        imei: replica.imei,
        imei_real: replica.imei_real,
        placa: replica.placa,
        fechaHoraInicial: replica.fechaHoraInicial,
        fechaHoraFinal: replica.fechaHoraFinal,
        puntos: replica.puntos,
        estado: replica.estado,
        estado_replica: replica.estado_replica,
        tipoSimulacion: replica.tipoSimulacion,
        nombre_ruta: `${replica.nombre_ruta} (Copia)`
      }

      // Guardar los datos en localStorage para que el formulario los use
      localStorage.setItem('datosReplicaDuplicada', JSON.stringify(datosReplica))
      
      // Redirigir al formulario de creación
      window.location.href = '/simulaciones-gps/nueva'
      
    } catch (error) {
      console.error('Error preparando réplica para duplicar:', error)
      toast.error('Error al preparar la réplica para duplicar')
    }
  }

  // Actualizar fechas masivamente (solo ids y fechaHoraFinal)
  const actualizarFechas = async () => {
    try {
      if (selectedIds.length === 0) {
        toast.error('Debe seleccionar al menos una réplica')
        return
      }
      if (!nuevaFechaHoraFinal) {
        toast.error('Debe especificar una nueva fecha y hora final')
        return
      }
      await simulacionesGpsService.actualizarFechas(selectedIds, nuevaFechaHoraFinal)
      toast.success(`Fechas actualizadas exitosamente para ${selectedIds.length} réplicas`)
      setShowUpdateModal(false)
      setNuevaFechaHoraFinal('')
      setSelectedIds([])
      setSelectAll(false)
      cargarReplicas()
    } catch (error) {
      console.error('Error actualizando fechas:', error)
      toast.error('Error al actualizar las fechas')
    }
  }

  // Manejar selección individual
  const handleSelectReplica = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id)
      } else {
        return [...prev, id]
      }
    })
  }

  // Manejar selección de todas
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([])
      setSelectAll(false)
    } else {
      setSelectedIds(replicasPagina.map(r => r.id))
      setSelectAll(true)
    }
  }

  // Obtener estado visual
  const getEstadoVisual = (replica) => {
    const fechaFinal = new Date(replica.fechaHoraFinal)
    const ahora = new Date()
    if (fechaFinal > ahora) {
      return { 
        icon: CheckCircle, 
        color: 'text-green-600', 
        bgColor: 'bg-green-100',
        text: 'Activo'
      }
    } else {
      return { 
        icon: XCircle, 
        color: 'text-red-600', 
        bgColor: 'bg-red-100',
        text: 'Inactivo'
      }
    }
  }

  // Ordenar por columna
  const handleOrdenamiento = (campo) => {
    if (!columnasOrdenables.includes(campo)) return
    setOrdenamiento(prev => {
      if (prev.campo === campo) {
        return { campo, direccion: prev.direccion === 'asc' ? 'desc' : 'asc' }
      } else {
        return { campo, direccion: 'asc' }
      }
    })
    setPagina(1)
  }

  // Icono de ordenamiento
  const getOrdenamientoIcono = (campo) => {
    if (ordenamiento.campo !== campo) return null
    return ordenamiento.direccion === 'asc' ? '▲' : '▼'
  }

  // Cambiar página
  const handlePageChange = (nuevaPagina) => {
    if (nuevaPagina < 1 || nuevaPagina > totalPaginas) return
    setPagina(nuevaPagina)
    setSelectAll(false)
    setSelectedIds([])
  }

  // Generar botones de paginación numerados
  const renderPagination = () => (
    <div className="flex justify-between items-center px-6 py-4 border-t border-gray-100">
      <div className="text-sm text-gray-500">
        Página {pagina} de {totalPaginas} ({replicasOrdenadas.length} réplicas)
      </div>
      <div className="flex gap-1">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => handlePageChange(1)}
          disabled={pagina === 1}
        >
          {'<<'}
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => handlePageChange(pagina - 1)}
          disabled={pagina === 1}
        >
          {'<'}
        </button>
        {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            className={`btn btn-sm ${p === pagina ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>
        ))}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => handlePageChange(pagina + 1)}
          disabled={pagina === totalPaginas}
        >
          {'>'}
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => handlePageChange(totalPaginas)}
          disabled={pagina === totalPaginas}
        >
          {'>>'}
        </button>
      </div>
    </div>
  )

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Réplicas GPS</h1>
          <p className="text-gray-600">Administra las réplicas de simulaciones GPS</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.location.href = '/simulaciones-gps'}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            title="Ir a la gestión de simulaciones GPS"
          >
            <RotateCcw className="w-5 h-5" />
            Ver Simulaciones
          </button>
          {hasPermission('ACTUALIZAR_FECHAS_GPS') && (
          <button
            onClick={() => setShowUpdateModal(true)}
            disabled={selectedIds.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            title="Actualizar fecha y hora final de las réplicas seleccionadas"
          >
            <Calendar className="w-5 h-5" />
            Actualizar Fechas ({selectedIds.length})
          </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado Réplica
              <span className="ml-1 text-gray-400" title="Estado de la réplica: Activo = Réplica funcionando, Inactivo = Réplica pausada">
                ⓘ
              </span>
            </label>
            <select
              value={filtros.estado_replica}
              onChange={(e) => setFiltros({...filtros, estado_replica: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
            >
              <option value="">Todos los estados</option>
              <option value="1">Activo</option>
              <option value="0">Inactivo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Inicio
              <span className="ml-1 text-gray-400" title="Filtrar réplicas desde esta F. Hora Final">
                ⓘ
              </span>
            </label>
            <input
              type="date"
              value={filtros.fecha_inicio}
              onChange={(e) => setFiltros({...filtros, fecha_inicio: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Fin
              <span className="ml-1 text-gray-400" title="Filtrar réplicas hasta esta F. Hora Final">
                ⓘ
              </span>
            </label>
            <input
              type="date"
              value={filtros.fecha_fin}
              onChange={(e) => setFiltros({...filtros, fecha_fin: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Búsqueda
              <span className="ml-1 text-gray-400" title="Buscar en IMEI, placa, nombre de ruta y otros campos">
                ⓘ
              </span>
            </label>
            <input
              type="text"
              placeholder="Buscar..."
              value={filtros.busqueda}
              onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFiltros({ estado_replica: '', fecha_inicio: '', fecha_fin: '', busqueda: '' })}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-xs"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('id')}>
                    ID {getOrdenamientoIcono('id')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('fechaLanzamiento')}>
                    F. Ejecución {getOrdenamientoIcono('fechaLanzamiento')}
                    <span className="ml-1 text-gray-400" title="Fecha cuando se lanzó la simulación">
                      ⓘ
                    </span>
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('imei')}>
                    IMEI SIM {getOrdenamientoIcono('imei')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('imei_real')}>
                    IMEI Real {getOrdenamientoIcono('imei_real')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('placa')}>
                    Placa {getOrdenamientoIcono('placa')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('estado_replica')}>
                    E. Réplica {getOrdenamientoIcono('estado_replica')}
                    <span className="ml-1 text-gray-400" title="Estado de la réplica: Verde = Activo (funcionando), Rojo = Inactivo (pausado)">
                      ⓘ
                    </span>
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleOrdenamiento('fechaHoraFinal')}>
                    F. Hora Final {getOrdenamientoIcono('fechaHoraFinal')}
                    <span className="ml-1 text-gray-400" title="Fecha y hora cuando termina la simulación (determina si está activa o inactiva)">
                      ⓘ
                    </span>
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {replicasPagina.map((replica) => {
                  const estadoVisual = getEstadoVisual(replica)
                  return (
                    <tr key={replica.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(replica.id)}
                          onChange={() => handleSelectReplica(replica.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap font-medium text-gray-900">
                        {replica.id}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-500">
                        {formatearFechaParaMostrar(replica.fechaLanzamiento)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-900">
                        {replica.imei}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-900">
                        {replica.imei_real}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-900">
                        {replica.placa}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${estadoVisual.bgColor} ${estadoVisual.color} text-xs`}>
                          <estadoVisual.icon className="w-4 h-4 mr-1" />
                          {replica.estado_replica === '1' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-500">
                        {formatearFechaParaMostrar(replica.fechaHoraFinal)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap font-medium">
                        <div className="flex space-x-2">
                          {hasPermission('VER_REPLICAS_GPS') && (
                          <button
                            onClick={() => cargarReplica(replica.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Ver detalles de la réplica"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          )}
                          {hasPermission('COPIAR_REPLICAS') && (
                          <button
                            onClick={() => duplicarReplica(replica.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Copiar réplica (abre formulario con datos prellenados)"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                          )}
                          {hasPermission('ELIMINAR_REPLICAS') && (
                          <button
                            onClick={() => {
                              setDeleteId(replica.id)
                              setShowDeleteModal(true)
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar réplica permanentemente"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {/* Paginación */}
            {renderPagination()}
          </div>
        )}
      </div>

      {/* Modal de detalles */}
      {showModal && selectedReplica && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Detalles de Réplica</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">ID</label>
                <p className="text-sm text-gray-900">{selectedReplica.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de Ruta</label>
                <p className="text-sm text-gray-900">{selectedReplica.nombre_ruta}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">IMEI Simulado</label>
                <p className="text-sm text-gray-900">{selectedReplica.imei}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">IMEI Real</label>
                <p className="text-sm text-gray-900">{selectedReplica.imei_real}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Placa</label>
                <p className="text-sm text-gray-900">{selectedReplica.placa}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo de Simulación</label>
                <p className="text-sm text-gray-900">{selectedReplica.tipoSimulacion}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha de Lanzamiento</label>
                <p className="text-sm text-gray-900">{formatearFechaParaMostrar(selectedReplica.fechaLanzamiento)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha y Hora Inicial</label>
                <p className="text-sm text-gray-900">{formatearFechaParaMostrar(selectedReplica.fechaHoraInicial)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha y Hora Final</label>
                <p className="text-sm text-gray-900">{formatearFechaParaMostrar(selectedReplica.fechaHoraFinal)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Estado</label>
                <p className="text-sm text-gray-900">{selectedReplica.estado === '1' ? 'Activo' : 'Inactivo'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Estado Réplica</label>
                <p className="text-sm text-gray-900">{selectedReplica.estado_replica === '1' ? 'Activo' : 'Inactivo'}</p>
              </div>
              
              {/* Configuración de Plataformas */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Configuración de Plataformas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Plataforma Origen */}
                  <div className="bg-blue-50 p-3 rounded">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Origen</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">URL Tracker</label>
                        <p className="text-xs text-gray-900 break-all">{selectedReplica.tracker_url_origen || 'https://gps.tecnytte.com'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">API Key</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedReplica.api_key_origen || '4C992B2947DFF375E4D75337E9EB5AB1'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">User Token</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedReplica.user_token_origen || '29A51402D29359061D885496BBFD5CC7'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Plataforma Destino */}
                  <div className="bg-green-50 p-3 rounded">
                    <h4 className="text-sm font-semibold text-green-900 mb-2">Destino</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">URL Tracker</label>
                        <p className="text-xs text-gray-900 break-all">{selectedReplica.tracker_url_destino || 'https://gps.tecnytte.com'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">API Key</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedReplica.api_key_destino || '4C992B2947DFF375E4D75337E9EB5AB1'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">User Token</label>
                        <p className="text-xs text-gray-900 font-mono break-all">{selectedReplica.user_token_destino || '29A51402D29359061D885496BBFD5CC7'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Puntos de Ruta (JSON)</label>
                <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                  {(() => {
                    try {
                      // Si el campo está vacío o solo tiene un punto, mostrar el valor original
                      if (!selectedReplica.puntos || selectedReplica.puntos.trim() === '' || selectedReplica.puntos.trim() === '.') {
                        return selectedReplica.puntos || 'Campo vacío';
                      }
                      // Intentar parsear JSON
                      const parsed = JSON.parse(selectedReplica.puntos);
                      return JSON.stringify(parsed, null, 2);
                    } catch (error) {
                      // Si falla el parse, mostrar el valor original
                      return selectedReplica.puntos || 'Error al parsear JSON';
                    }
                  })()}
                </pre>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => window.location.href = `/simulaciones-gps/editar/${selectedReplica.id}`}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Editar
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              <h2 className="text-xl font-bold">Confirmar Eliminación</h2>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Está seguro de que desea eliminar esta réplica? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={eliminarReplica}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de actualización de fechas */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <Calendar className="w-6 h-6 text-green-600 mr-2" />
              <h2 className="text-xl font-bold">Actualizar Fechas</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Actualizar fecha y hora final para {selectedIds.length} réplica(s) seleccionada(s).
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nueva Fecha y Hora Final
              </label>
              <input
                type="datetime-local"
                value={nuevaFechaHoraFinal}
                onChange={(e) => setNuevaFechaHoraFinal(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowUpdateModal(false)
                  setNuevaFechaHoraFinal('')
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={actualizarFechas}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                Actualizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionReplicasGPS 